import { useState, useEffect } from 'react';
import { CourseDetailsResponse } from '../../interfaces/api/courses';
import { services } from '../../api';

/**
 * Custom hook for managing course details state and fetching course data.
 * Provides course details, loading state, and a method to update course details.
 *
 * @param {number} id - The ID of the course to fetch details for.
 * @returns {{
 *   courseDetails: CourseDetailsResponse | null,
 *   loading: boolean,
 *   updateCourseDetails: (updatedDetails: CourseDetailsResponse) => void
 * }} Object containing course details, loading state, and an update function.
 *
 * @example
 * const { courseDetails, loading, updateCourseDetails } = useCourseDetails(123);
 * if (loading) return <LoadingSpinner />;
 * return <CourseView details={courseDetails} />;
 */
export const useCourseDetails = (
  id: number,
): {
  courseDetails: CourseDetailsResponse | null;
  loading: boolean;
  updateCourseDetails: (updatedDetails: CourseDetailsResponse) => void;
} => {
  const [courseDetails, setCourseDetails] = useState<CourseDetailsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    /**
     * Fetches course details from the API.
     * Updates the `courseDetails` and `loading` state accordingly.
     *
     * @async
     * @returns {Promise<void>}
     */
    const getDetails = async () => {
      setLoading(true);
      try {
        const res = await services.courses.getDetails(id);
        setCourseDetails(res.content || null);
      } catch (error) {
        console.error('Error fetching course details:', error);
      } finally {
        setLoading(false);
      }
    };

    getDetails();
  }, [id]);

  /**
   * Updates the course details state with new data.
   * Can be used to reflect changes such as video completion and unlocking.
   *
   * @param {CourseDetailsResponse} updatedDetails - The updated course details.
   */
  const updateCourseDetails = (updatedDetails: CourseDetailsResponse) => {
    setCourseDetails(updatedDetails);
  };

  return { courseDetails, loading, updateCourseDetails };
};
